import React, { useState, useEffect } from "react";
import { DashboardLayout } from "src/components/dashboard";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import Select from "react-select";

import {
  useAddDeliveryPartnerMutation,
  useGetAllPartnerCategoriesQuery,
  useGetPartnerCategoryByIdQuery,
} from "src/store/services/partnerApi";

interface CredentialField {
  label: string;
  key: string;
  value: string;
  is_secret: boolean;
}

interface FormData {
  environment: string;
  credential_fields: CredentialField[];
  outlets: string[];
}

const MIN_CREDENTIAL_FIELDS = 2;

const AddDeliveryPartner = () => {
  const navigate = useNavigate();

  const [addDeliveryPartner, { isLoading }] = useAddDeliveryPartnerMutation();

  const [error, setError] = useState<string | null>(null);

  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedCategoryOption, setSelectedCategoryOption] = useState<any>(null);

  // ======================
  // FETCH CATEGORIES
  // ======================
  const { data: categoriesData, isLoading: categoryLoading } =
    useGetAllPartnerCategoriesQuery();

  // ======================
  // FETCH PARTNERS
  // ======================
  const { data: partnersData, isLoading: partnersLoading } =
    useGetPartnerCategoryByIdQuery(
      { page: 1, id: selectedCategory, limit: 1000 },
      { skip: !selectedCategory },
    );

  // ======================
  // CATEGORY OPTIONS
  // ======================
  const categoryOptions =
    categoriesData?.payload?.data?.map((cat: any) => ({
      value: cat.category_id,
      label: cat.category_name,
    })) || [];

  useEffect(() => {
    if (categoryOptions.length && !selectedCategory) {
      const deliveryCategory = categoryOptions.find(
        (option: any) => option.label.toLowerCase() === "delivery",
      );

      if (deliveryCategory) {
        setSelectedCategory(deliveryCategory.value);
        setSelectedCategoryOption(deliveryCategory);
      }
    }
  }, [categoryOptions, selectedCategory]);

  // ======================
  // OUTLET OPTIONS
  // ======================
  const partnerOptions =
    partnersData?.payload?.partners?.map((p: any) => ({
      value: p._id || p.partner_id || p.id,
      label: p.business_name || p.name || "Unnamed Outlet",
    })) || [];

  // ======================
  // FORM STATE
  // ======================
  const [formData, setFormData] = useState<FormData>({
    environment: "test",
    outlets: [],
    credential_fields: [
      {
        label: "Merchant ID",
        key: "merchantId",
        value: "",
        is_secret: false,
      },
      {
        label: "API Key",
        key: "apiKey",
        value: "",
        is_secret: false,
      },
    ],
  });

  // ======================
  // ENVIRONMENT
  // ======================
  const handleEnvironmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData((prev) => ({
      ...prev,
      environment: e.target.value,
    }));
  };

  // ======================
  // FIELD CHANGE
  // ======================
  const handleFieldChange = (
    index: number,
    field: keyof CredentialField,
    value: string | boolean,
  ) => {
    const updated = [...formData.credential_fields];

    updated[index] = {
      ...updated[index],
      [field]: value,
    };

    setFormData((prev) => ({
      ...prev,
      credential_fields: updated,
    }));
  };

  // ======================
  // OUTLETS CHANGE
  // ======================
  const handleOutletChange = (option: any) => {
    setFormData((prev) => ({
      ...prev,
      outlets: option ? [option.value] : [],
    }));
  };

  // ======================
  // ADD FIELD
  // ======================
  const addCredentialField = () => {
    setFormData((prev) => ({
      ...prev,
      credential_fields: [
        ...prev.credential_fields,
        {
          label: "",
          key: "",
          value: "",
          is_secret: false,
        },
      ],
    }));
  };

  // ======================
  // REMOVE FIELD
  // ======================
  const removeCredentialField = (index: number) => {
    if (formData.credential_fields.length <= MIN_CREDENTIAL_FIELDS) {
      toast.warning(
        `At least ${MIN_CREDENTIAL_FIELDS} credential fields are required`,
      );
      return;
    }

    setFormData((prev) => ({
      ...prev,
      credential_fields: prev.credential_fields.filter((_, i) => i !== index),
    }));
  };

  // ======================
  // SUBMIT
  // ======================
  const handleSubmit = async () => {
    setError(null);

    if (!formData.environment) {
      setError("Environment is required");
      return;
    }

    if (!formData.outlets.length) {
      setError("Select at least one outlet");
      return;
    }

    if (formData.credential_fields.length < MIN_CREDENTIAL_FIELDS) {
      setError(`At least ${MIN_CREDENTIAL_FIELDS} credential fields are required`);
      return;
    }

    for (const field of formData.credential_fields) {
      if (!field.label || !field.key || !field.value) {
        setError("All credential fields are required");
        return;
      }
    }

    try {
      const payload = {
        environment: formData.environment,
        outlets: formData.outlets,
        credential_fields: formData.credential_fields,
      };

      console.log("PAYLOAD:", payload);

      await addDeliveryPartner({
        id: formData.outlets[0], // if API only accepts ONE outlet
        environment: formData.environment,
        credential_fields: formData.credential_fields,
      }).unwrap();

      toast.success("Credentials saved successfully");

      setTimeout(() => {
        navigate("/delivery_partner");
      }, 1000);
    } catch (err: any) {
      const message = err?.data?.message || err?.message || "Failed to save";

      setError(message);
      toast.error(message);
    }
  };

  const canRemoveFields = formData.credential_fields.length > MIN_CREDENTIAL_FIELDS;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto p-6 bg-white rounded-xl shadow-sm">
        <h2 className="text-2xl font-semibold mb-2">
          Delivery Partner Credentials
        </h2>

        <p className="text-gray-500 mb-8">Configure provider credentials.</p>

        {/* OUTLETS */}
        <div className="mb-4">
          <label>Delivery Partners</label>
          <Select
            options={partnerOptions}
            isLoading={partnersLoading}
            isDisabled={!selectedCategory}
            onChange={handleOutletChange}
          />
        </div>

        {/* ENVIRONMENT */}
        <div className="mb-6">
          <label>Environment</label>
          <select
            value={formData.environment}
            onChange={handleEnvironmentChange}
            className="w-full border p-3 rounded"
          >
            <option value="live">Live</option>
            <option value="test">Sandbox</option>
          </select>
        </div>

        {/* FIELDS */}
        {formData.credential_fields.map((field, index) => (
          <div key={index} className="border p-4 mb-4 rounded relative">
            <input
              placeholder="Label"
              value={field.label}
              onChange={(e) =>
                handleFieldChange(index, "label", e.target.value)
              }
              className="border p-2 w-full mb-2"
            />

            <input
              placeholder="Key"
              value={field.key}
              onChange={(e) => handleFieldChange(index, "key", e.target.value)}
              className="border p-2 w-full mb-2"
            />

            <input
              placeholder="Value"
              type={field.is_secret ? "password" : "text"}
              value={field.value}
              onChange={(e) =>
                handleFieldChange(index, "value", e.target.value)
              }
              className="border p-2 w-full mb-2"
            />

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={field.is_secret}
                  onChange={(e) =>
                    handleFieldChange(index, "is_secret", e.target.checked)
                  }
                />
                Secret
              </label>

              <button
                type="button"
                onClick={() => removeCredentialField(index)}
                disabled={!canRemoveFields}
                title={
                  !canRemoveFields
                    ? `At least ${MIN_CREDENTIAL_FIELDS} fields are required`
                    : "Remove this field"
                }
                className={`text-sm px-3 py-1 rounded border ${
                  !canRemoveFields
                    ? "text-gray-400 border-gray-200 cursor-not-allowed"
                    : "text-red-600 border-red-300 hover:bg-red-50"
                }`}
              >
                Remove
              </button>
            </div>
          </div>
        ))}

        {/* ADD FIELD */}
        <button
          type="button"
          onClick={addCredentialField}
          className="text-purple-600 border border-purple-300 hover:bg-purple-50 px-4 py-2 rounded mb-6"
        >
          + Add Field
        </button>

        {/* ERROR */}
        {error && <div className="text-red-600 mb-4">{error}</div>}

        {/* SUBMIT */}
        <button
          onClick={handleSubmit}
          disabled={isLoading}
          className="bg-purple-600 text-white w-full p-3 rounded"
        >
          {isLoading ? "Saving..." : "Save Credentials"}
        </button>

        <ToastContainer />
      </div>
    </DashboardLayout>
  );
};

export default AddDeliveryPartner;